import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import IsoDatePicker from '../../schedule/components/IsoDatePicker'
import MoneyInput from '../components/MoneyInput'
import {
  CONTRACT_KIND_KEY, contractFormProblem, contractInput, emptyContractForm, formFromContract, type ContractForm,
} from '../lib/contracts'
import type { CostContract } from '../types'

const Field: React.FC<{ id: string; label: string; required?: boolean; className?: string; children: React.ReactNode }> = ({ id, label, required, className, children }) => (
  <div className={cn('space-y-1.5', className)}>
    <Label htmlFor={id}>{label}{required ? ' *' : ''}</Label>
    {children}
  </div>
)

interface Props {
  open: boolean
  mode: 'create' | 'edit'
  contract?: CostContract | null
  currency: string
  /** A main contract may be created (one per project). */
  allowMain: boolean
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the input; the dialog stays open on failure. */
  onSubmit: (input: Record<string, unknown>) => Promise<boolean>
}

const ContractDialog: React.FC<Props> = ({ open, mode, contract, currency, allowMain, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<ContractForm>(emptyContractForm)
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(contract ? formFromContract(contract) : emptyContractForm())
    setTouched(false)
    setSubmitting(false)
    // Only on opening: a background reload of the contract must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = <K extends keyof ContractForm>(key: K, value: ContractForm[K]) => setForm((f) => ({ ...f, [key]: value }))
  const problem = contractFormProblem(form, contract?.status)
  const submit = async () => {
    setTouched(true)
    if (problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit(contractInput(form, mode))
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t(mode === 'create' ? 'CostControl_contract_new' : 'CostControl_contract_edit')}</DialogTitle>
          <DialogDescription>{t('CostControl_contract_dialog_hint')}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit() }}>
          {mode === 'create' && allowMain && (
            <Field id="cc-kind" label={t('CostControl_contract_field_kind')} className="sm:col-span-2">
              <Select value={form.kind} onValueChange={(v) => set('kind', v as ContractForm['kind'])}>
                <SelectTrigger id="cc-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="SUB">{t(CONTRACT_KIND_KEY.SUB)}</SelectItem>
                  <SelectItem value="MAIN">{t(CONTRACT_KIND_KEY.MAIN)}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field id="cc-name" label={t('CostControl_contract_field_name')} required className="sm:col-span-2">
            <Input id="cc-name" value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />
          </Field>
          <Field id="cc-counterparty" label={t('CostControl_contract_field_counterparty')} required>
            <Input id="cc-counterparty" value={form.counterpartyName} onChange={(e) => set('counterpartyName', e.target.value)} />
          </Field>
          <Field id="cc-ico" label={t('CostControl_contract_field_ico')}>
            <Input id="cc-ico" value={form.counterpartyIco} onChange={(e) => set('counterpartyIco', e.target.value)} />
          </Field>
          <Field id="cc-number" label={t('CostControl_contract_field_number')}>
            <Input id="cc-number" value={form.number} onChange={(e) => set('number', e.target.value)} />
          </Field>
          {form.kind === 'SUB' && (
            <Field id="cc-retention" label={t('CostControl_contract_field_retentionPercent')}>
              <MoneyInput id="cc-retention" value={form.retentionPercent} onChange={(value, valid) => setForm((f) => ({ ...f, retentionPercent: value, retentionValid: valid }))} />
            </Field>
          )}
          {form.kind === 'MAIN' && (
            <Field id="cc-value" label={`${t('CostControl_contract_field_mainValue')} (${currency})`}>
              <MoneyInput id="cc-value" value={form.mainValue} onChange={(value, valid) => setForm((f) => ({ ...f, mainValue: value, mainValueValid: valid }))} />
            </Field>
          )}
          <Field id="cc-signed" label={t('CostControl_contract_field_signedAt')}>
            <IsoDatePicker id="cc-signed" value={form.signedAt} onChange={(v) => set('signedAt', v)} clearable />
          </Field>
          <Field id="cc-start" label={t('CostControl_contract_field_startDate')}>
            <IsoDatePicker id="cc-start" value={form.startDate} onChange={(v) => set('startDate', v)} clearable />
          </Field>
          <Field id="cc-end" label={t('CostControl_contract_field_endDate')}>
            <IsoDatePicker id="cc-end" value={form.endDate} onChange={(v) => set('endDate', v)} clearable invalid={touched && problem === 'CostControl_error_dates_order'} />
          </Field>
          <Field id="cc-notes" label={t('CostControl_contract_field_notes')} className="sm:col-span-2">
            <Textarea id="cc-notes" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </Field>
          {touched && problem && <p className="text-sm text-destructive sm:col-span-2">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t(mode === 'create' ? 'Common_create' : 'Common_save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default ContractDialog
